import { Box, Typography, IconButton, Menu, Link, CircularProgress } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import Image from 'next/image';
import { useAccountMenu } from 'src/Core/hooks/useAccountMenu';
import { trpcClient } from 'pages/api/trpc/_api';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';

const BRAND_GREEN = '#325127';
const LINK_GREEN = '#75956a';
const DROPDOWN_BG = '#f0eee7';
const BORDER_COLOR = '#999';

interface UserIdentity {
  name: string;
  email: string;
  organization: {
    name: string;
  };
}

interface UserClaimant {
  accountManager: {
    fullName: string;
    email: string;
    workPhone: string;
  };
}

function formatSsn(ssn: string): string {
  if (ssn.length === 10) {
    return `${ssn.slice(0, 6)}-${ssn.slice(6)}`;
  }
  return ssn;
}

export default function AccountMenu({ isMobile = false }) {
  const { handleOpenAccountMenu, handleCloseAccountMenu, handleLogout, isOpen, anchorElUser } = useAccountMenu();
  const [userData, setUserData] = useState<UserIdentity | null>(null);
  const [claimantData, setClaimantData] = useState<UserClaimant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { data: session } = useSession();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const userIdentity = await trpcClient.identity.getIdentity.query().catch(() => null);
      const userClaimant = await trpcClient.claimant.getClaimant.query().catch(() => null);

      if (userIdentity) setUserData(userIdentity);
      if (userClaimant) setClaimantData(userClaimant);

      setIsLoading(false);
    };

    fetchData();
  }, []);

  const ssn = session?.user?.sub ?? '';

  function renderAccountManagerSection() {
    if (claimantData?.accountManager) {
      return (
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER_COLOR}` }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: BRAND_GREEN, pb: 0.5 }}>
            Viðskiptastjórinn þinn er
          </Typography>
          <Typography sx={{ fontSize: 18, color: BRAND_GREEN }}>{claimantData.accountManager.fullName}</Typography>
          <Typography sx={{ fontSize: 18, color: BRAND_GREEN }}>S. {claimantData.accountManager.workPhone}</Typography>
          <Link href={`mailto:${claimantData.accountManager.email}`} underline="hover" color={LINK_GREEN}>
            <Typography sx={{ fontSize: 18 }}>{claimantData.accountManager.email}</Typography>
          </Link>
        </Box>
      );
    }
    return (
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER_COLOR}` }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: BRAND_GREEN, pb: 0.5 }}>Vantar þig aðstoð?</Typography>
        <Typography sx={{ fontSize: 18, color: BRAND_GREEN }}>
          Spjallaðu við okkur á netspjallinu eða sendu okkur fyrirspurn í gegnum það.
        </Typography>
      </Box>
    );
  }

  function renderLinkItem(label: string, onClick: () => void) {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(172,184,144,0.2)' },
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: LINK_GREEN }}>{label}</Typography>
        <ArrowForwardIosIcon sx={{ fontSize: 16, color: LINK_GREEN }} />
      </Box>
    );
  }

  // Mobile dropdown (full-width overlay)
  function renderMobileDropdown() {
    if (!isOpen) return null;
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '80px',
          right: 0,
          left: 0,
          width: '100%',
          backgroundColor: DROPDOWN_BG,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000,
        }}
      >
        {/* User name + SSN */}
        <Box sx={{ px: 3, pt: 3, pb: 2, textAlign: 'center', borderBottom: `1px solid ${BORDER_COLOR}` }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: BRAND_GREEN }}>{userData?.name}</Typography>
              {ssn && <Typography sx={{ fontSize: 18, color: BRAND_GREEN, mt: 0.5 }}>{formatSsn(ssn)}</Typography>}
            </>
          )}
        </Box>

        {/* Account manager */}
        {renderAccountManagerSection()}

        {/* Links */}
        {renderLinkItem('Þínar upplýsingar', () => {
          handleCloseAccountMenu();
          router.push('/stillingar');
        })}
        {/* {renderLinkItem('Skilmálar', () => {
          handleCloseAccountMenu();
        })} */}

        {/* Logout button */}
        <Box sx={{ px: 3, py: 2 }}>
          <Box
            onClick={handleLogout}
            sx={{
              bgcolor: BRAND_GREEN,
              color: '#fff',
              textAlign: 'center',
              py: 1.2,
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 18,
              fontWeight: 600,
              '&:hover': { bgcolor: '#3d6330' },
            }}
          >
            Útskrá
          </Box>
        </Box>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexGrow: 1,
          alignItems: 'center',
        }}
      >
        <IconButton onClick={handleOpenAccountMenu} sx={{ p: 0.5 }}>
          {isOpen ? (
            <CloseIcon sx={{ color: 'white' }} />
          ) : (
            <Image src="/icons/accountMenuIconV2white.svg" alt="menu" width="32" height="32" />
          )}
        </IconButton>
        {renderMobileDropdown()}
      </Box>
    );
  }

  return (
    <>
      <IconButton
        onClick={handleOpenAccountMenu}
        sx={{
          p: '8px',
          bgcolor: '#fff',
          border: `1px solid ${BRAND_GREEN}`,
          borderRadius: '4px',
          width: 40,
          height: 40,
          '&:hover': { bgcolor: '#f5f5f0' },
        }}
      >
        <PersonOutlineIcon sx={{ fontSize: 24, color: BRAND_GREEN }} />
      </IconButton>
      <Menu
        sx={{
          '& .MuiPaper-root': {
            width: 400,
            bgcolor: DROPDOWN_BG,
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          },
          '& .MuiList-root': {
            padding: 0,
          },
        }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseAccountMenu}
        disableScrollLock
      >
        {/* User name + SSN */}
        <Box sx={{ px: 3, pt: 3, pb: 2, textAlign: 'center', borderBottom: `1px solid ${BORDER_COLOR}` }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography sx={{ fontSize: 21, fontWeight: 700, color: BRAND_GREEN }}>{userData?.name}</Typography>
              {ssn && <Typography sx={{ fontSize: 18, color: BRAND_GREEN, mt: 0.5 }}>{formatSsn(ssn)}</Typography>}
            </>
          )}
        </Box>

        {/* Account manager */}
        {renderAccountManagerSection()}

        {/* Links */}
        {renderLinkItem('Þínar upplýsingar', () => {
          handleCloseAccountMenu();
          router.push('/stillingar');
        })}
        {/* {renderLinkItem('Skilmálar', () => {
          handleCloseAccountMenu();
        })} */}

        {/* Logout button */}
        <Box sx={{ px: 3, py: 2 }}>
          <Box
            onClick={handleLogout}
            sx={{
              bgcolor: BRAND_GREEN,
              color: '#fff',
              textAlign: 'center',
              py: 1.2,
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 18,
              fontWeight: 600,
              '&:hover': { bgcolor: '#3d6330' },
            }}
          >
            Útskrá
          </Box>
        </Box>
      </Menu>
    </>
  );
}
